import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { Chart } from "react-google-charts";
import { useTable, useFilters } from "react-table";
import AnimatedTaps from "../components/AnimatedTaps";
import TankStatus from "../components/TankStatus"; 
import "../styles/Dashboard.css";

// Base URL dari .env
const API_BASE_URL = process.env.REACT_APP_API_URL || "https://depotaircerdas.com/api";

const DailyReport = ({ username }) => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    const [data, setData] = useState(null);
    const [tankData, setTankData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(currentMonth);

    // Daftar bulan
    const months = [
        { value: "2025-01", label: "Januari 2025" },
        { value: "2025-02", label: "Februari 2025" },
        { value: "2025-03", label: "Maret 2025" },
        { value: "2025-04", label: "April 2025" },
        { value: "2025-05", label: "Mei 2025" },
        { value: "2025-06", label: "Juni 2025" },
        { value: "2025-07", label: "Juli 2025" },
        { value: "2025-08", label: "Agustus 2025" },
        { value: "2025-09", label: "September 2025" },
        { value: "2025-10", label: "Oktober 2025" },
        { value: "2025-11", label: "November 2025" },
        { value: "2025-12", label: "Desember 2025" },
    ];

    // 🔹 Ambil Token dari Local Storage
    const getToken = () => {
        const token = localStorage.getItem("token");
        console.log("📌 Token di Local Storage:", token); // Cek token sebelum digunakan
        if (!token) return null;
        return `Bearer ${token}`;
    };
    

    // **Fungsi Fetch Data dengan useCallback**
    const fetchData = useCallback(async () => {
        const token = getToken();
        if (!token) {
            console.warn("🚨 Token tidak ditemukan. Redirect ke login.");
            window.location.href = "/login"; 
            return;
        }
    
        console.log("📌 Token yang dikirim di header:", token); // Tambahkan log
    
        try {
            const res = await axios.get(`${API_BASE_URL}/reports/${username}/daily?month=${selectedMonth}`, {
                headers: { 
                    "Authorization": token, 
                    "Content-Type": "application/json"
                }
            });
            console.log("🚀 API Response:", res.data);
            setData(res.data);
    
            const tankRes = await axios.get(`${API_BASE_URL}/tanks/${username}`, {
                headers: { Authorization: token }
            });
            setTankData(tankRes.data);
        } catch (error) {
            console.error("❌ Error fetching data:", error);
            setError("Gagal mengambil data.");
        } finally {
            setLoading(false);
        }
    }, [username, selectedMonth]);
    

    // 🔄 **Fetch Data saat Komponen Dimuat & Auto-refresh Tiap 1 Menit**
    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 60000);
        return () => clearInterval(interval);
    }, [fetchData]); // ✅ **Tambahkan fetchData sebagai dependensi**

    // **Memproses Data**
    const groupedData = useMemo(() => {
        if (!data?.daily) return [];
    
        return data.daily
            .map(d => ({
                date: new Date(d.date).toLocaleDateString("id-ID", { 
                    timeZone: "Asia/Jakarta",
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit"
                }),
                volume: d.volume ? parseFloat(d.volume).toFixed(2) : "0.00",
                gallons: d.gallons || 0,
                tapA: d.tapA || 0,
                tapB: d.tapB || 0,
                tapC: d.tapC || 0
            }))
            .sort((a, b) => new Date(a.date) - new Date(b.date));
    }, [data]);
    
    // Cek apakah data bulan ini kosong
    const isDataEmpty = groupedData.length === 0;

    // **Kolom Tabel**
    const columns = useMemo(() => [
        { Header: "Tanggal", accessor: "date" },
        { Header: "Volume (L)", accessor: "volume" },
        { Header: "Total Galon", accessor: "gallons" },
        { Header: "Keran A (Galon)", accessor: "tapA" },
        { Header: "Keran B (Galon)", accessor: "tapB" },
        { Header: "Keran C (Galon)", accessor: "tapC" }
    ], []);

    const tableInstance = useTable({ columns, data: groupedData }, useFilters);
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;
    
    return (
        <div className="report-container">
            <h3 className="report-title">📊 Laporan Harian - {username}</h3>
            <label>Pilih Bulan: </label>
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                {months.map((month) => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                ))}
            </select>

            {loading ? (
                <p className="loading">Memuat data...</p>
            ) : error ? (
                <p className="error-message">{error}</p>
            ) : isDataEmpty ? (
                <p className="error-message">⚠️ Data bulan ini kosong.</p>
            ) : (
                <>
                    {/* **Grafik Volume & Galon** */}
                    <Chart
                        chartType="LineChart"
                        width="100%"
                        height="350px"
                        data={[
                            ["Tanggal", "Volume (L)", "Galon"],
                            ...groupedData.map(d => [d.date, Number(d.volume), d.gallons])
                        ]}
                        options={{
                            title: "Trend Penggunaan Air (Harian)",
                            curveType: "function",
                            legend: { position: "top" },
                            colors: ["#007bff", "#dc3545"],
                            series: { 0: { targetAxisIndex: 0 }, 1: { targetAxisIndex: 1 } },
                            vAxes: {
                                0: { title: "Volume (L)", textStyle: { color: "#007bff" } },
                                1: { title: "Galon", textStyle: { color: "#dc3545" } }
                            }
                        }}
                    />

                    {/* **Tampilan Animasi Keran & Tangki** */}
                    <div className="tap-and-tank-wrapper">
                        <div className="animated-taps">
                            <AnimatedTaps
                                tapA={data?.tapA || 0}
                                tapB={data?.tapB || 0}
                                tapC={data?.tapC || 0}
                            />
                        </div>
                        <TankStatus tankData={tankData} />
                    </div>

                    {/* **Tabel Data** */}
                    <table {...getTableProps()} className="dashboard-table">
                        <thead>
                            {headerGroups.map(headerGroup => (
                                <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                                    {headerGroup.headers.map(column => (
                                        <th {...column.getHeaderProps()} key={column.id}>{column.render("Header")}</th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {rows.map(row => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()} key={row.id}>
                                        {row.cells.map(cell => (
                                            <td {...cell.getCellProps()} key={cell.column.id}>{cell.render("Cell")}</td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
};

export default DailyReport;
